import { httpTransport } from '../transport'
import type { RequestOptions } from '../transport/types'

/**
 * Helper to ensure backward compatibility for legacy callers expecting
 * Express envelope properties like { success: true, data: T }
 */
function attachCompatibilityProperties<T>(result: T): T {
  if (result && typeof result === 'object') {
    if (!('success' in result)) {
      try {
        Object.defineProperty(result, 'success', {
          value: true,
          writable: true,
          configurable: true,
          enumerable: false,
        })
      } catch {
        // Ignore if non-extensible
      }
    }
    if (!('data' in result)) {
      try {
        Object.defineProperty(result, 'data', {
          value: result,
          writable: true,
          configurable: true,
          enumerable: false,
        })
      } catch {
        // Ignore if non-extensible
      }
    }
  }
  return result
}

/**
 * ApiService backward-compatibility adapter.
 * Delegates all generic and domain calls to the deepened Tier 1 HttpTransport module.
 */
export class ApiService {
  // Generic HTTP methods delegating to Tier 1 HttpTransport
  async get<T>(url: string, config?: any): Promise<T> {
    const result = await httpTransport.get<T>(url, {
      params: config?.params,
      headers: config?.headers,
      signal: config?.signal,
    })
    return attachCompatibilityProperties(result)
  }

  async post<T>(url: string, data?: any, config?: any): Promise<T> {
    const result = await httpTransport.post<T>(url, data, {
      params: config?.params,
      headers: config?.headers,
      signal: config?.signal,
    })
    return attachCompatibilityProperties(result)
  }

  async put<T>(url: string, data?: any, config?: any): Promise<T> {
    const result = await httpTransport.put<T>(url, data, {
      params: config?.params,
      headers: config?.headers,
      signal: config?.signal,
    })
    return attachCompatibilityProperties(result)
  }

  async patch<T>(url: string, data?: any, config?: any): Promise<T> {
    const result = await httpTransport.patch<T>(url, data, {
      params: config?.params,
      headers: config?.headers,
      signal: config?.signal,
    })
    return attachCompatibilityProperties(result)
  }

  async delete<T>(url: string, config?: any): Promise<T> {
    const result = await httpTransport.delete<T>(url, {
      params: config?.params,
      headers: config?.headers,
      signal: config?.signal,
    })
    return attachCompatibilityProperties(result)
  }

  // Dashboard API methods
  async fetchKPIData(filters: Record<string, any> = {}) {
    return this.get('/dashboard/kpis', { params: filters })
  }

  async fetchRealtimeData() {
    return this.get('/dashboard/realtime')
  }

  async fetchEquipmentPerformance(filters: Record<string, any> = {}) {
    return this.get('/dashboard/equipment-performance', { params: filters })
  }

  async fetchAlertSummary(limit: number = 50) {
    return this.get('/dashboard/alerts', { params: { limit } })
  }

  // Equipment API methods
  async fetchEquipment(filters: Record<string, any> = {}) {
    return this.get('/equipment', { params: filters })
  }

  async fetchEquipmentById(id: string) {
    return this.get(`/equipment/${id}`)
  }

  async createEquipment(data: any) {
    return this.post('/equipment', data)
  }

  async updateEquipment(id: string, data: any) {
    return this.put(`/equipment/${id}`, data)
  }

  async deleteEquipment(id: string) {
    return this.delete(`/equipment/${id}`)
  }

  async fetchEquipmentByDepartment(departmentId: string) {
    return this.get(`/equipment/by-department/${departmentId}`)
  }

  async fetchEquipmentByStatus(status: string) {
    return this.get(`/equipment/by-status/${status}`)
  }

  async fetchEquipmentByType(typeId: string) {
    return this.get(`/equipment/by-type/${typeId}`)
  }

  async fetchEquipmentDueForMaintenance(daysAhead: number = 7) {
    return this.get(`/equipment/due-for-maintenance`, { params: { daysAhead } })
  }

  async updateEquipmentMaintenanceDates(id: string, lastMaintenanceDate: string, nextMaintenanceDate: string) {
    return this.put(`/equipment/${id}/maintenance-dates`, { lastMaintenanceDate, nextMaintenanceDate })
  }

  async searchEquipment(query: string, limit: number = 10) {
    return this.get(`/equipment/search`, { params: { q: query, limit } })
  }

  // Global search across all entities
  async globalSearch(query: string, limit: number = 20) {
    return this.get('/search/global', { params: { q: query, limit } })
  }

  async fetchEquipmentStats() {
    return this.get('/equipment/stats')
  }

  // Equipment Type API methods
  async fetchEquipmentTypes(filters: Record<string, any> = {}) {
    return this.get('/equipment-types', { params: filters })
  }

  async fetchEquipmentTypeById(id: string) {
    return this.get(`/equipment-types/${id}`)
  }

  async createEquipmentType(data: any) {
    return this.post('/equipment-types', data)
  }

  async updateEquipmentType(id: string, data: any) {
    return this.put(`/equipment-types/${id}`, data)
  }

  async deleteEquipmentType(id: string) {
    return this.delete(`/equipment-types/${id}`)
  }

  async fetchEquipmentTypesWithCounts() {
    return this.get('/equipment-types/with-counts')
  }

  // Inspection API methods
  async fetchInspections(filters: Record<string, any> = {}) {
    return this.get('/inspections', { params: filters })
  }

  async fetchInspectionById(id: string) {
    return this.get(`/inspections/${id}`)
  }

  async createInspection(data: any) {
    return this.post('/inspections', data)
  }

  async updateInspection(id: string, data: any) {
    return this.put(`/inspections/${id}`, data)
  }

  // Inspection Route API methods
  async fetchInspectionRoutes(filters: Record<string, any> = {}) {
    return this.get('/inspection-routes', { params: filters })
  }

  async fetchInspectionRouteById(id: string) {
    return this.get(`/inspection-routes/${id}`)
  }

  async createInspectionRoute(data: any) {
    return this.post('/inspection-routes', data)
  }

  async deleteInspectionRoute(id: string) {
    return this.delete(`/inspection-routes/${id}`)
  }

  async syncInspectionRouteStatus(id: string) {
    return this.post(`/inspection-routes/${id}/sync-status`, {})
  }

  async fetchInspectionRouteTracking(id: string) {
    return this.get(`/inspection-routes/${id}/tracking`)
  }

  async generateNextInspectionRouteRun(id: string) {
    return this.post(`/inspection-routes/${id}/generate-next`, {})
  }

  async duplicateInspectionRoute(id: string, data: Record<string, any> = {}) {
    return this.post(`/inspection-routes/${id}/duplicate`, data)
  }

  async updateInspectionStatus(id: string, status: string, approvedBy?: string) {
    return this.put(`/inspections/${id}/status`, { status, approvedBy })
  }

  async submitInspection(id: string, data: any) {
    return this.post(`/inspections/${id}/submit`, data)
  }

  async fetchInspectionsByEquipment(equipmentId: string) {
    return this.get(`/inspections/by-equipment/${equipmentId}`)
  }

  async fetchInspectionsByInspector(inspectorId: string) {
    return this.get(`/inspections/by-inspector/${inspectorId}`)
  }

  async fetchOverdueInspections() {
    return this.get('/inspections/overdue')
  }

  async fetchInspectionStats() {
    return this.get('/inspections/stats')
  }

  // Advanced inspection analytics for enterprise dashboard
  async fetchInspectionAnalytics(days: number = 30) {
    return this.get('/inspections/analytics', { params: { days } })
  }

  // Start an inspection
  async startInspection(id: string) {
    return this.put(`/inspections/${id}/start`, {})
  }

  // Approve an inspection
  async approveInspection(id: string, notes?: string) {
    return this.post(`/inspections/${id}/approve`, { notes })
  }

  // Reject an inspection with reason
  async rejectInspection(id: string, reason: string) {
    return this.post(`/inspections/${id}/reject`, { reason })
  }

  // Get inspection analysis/alerts
  async fetchInspectionAnalysis(id: string) {
    return this.get(`/inspections/${id}/analysis`)
  }

  // Get inspection history and trends
  async fetchInspectionHistory(id: string, days: number = 30) {
    return this.get(`/inspections/${id}/history`, { params: { days } })
  }

  // Get inspection report
  async fetchInspectionReport(id: string, format: 'json' | 'csv' | 'pdf' = 'json') {
    return this.get(`/inspections/${id}/report`, { params: { format } })
  }

  // Inspection Schedule API methods
  async fetchInspectionSchedules(filters: Record<string, any> = {}) {
    return this.get('/inspection-schedules', { params: filters })
  }

  async fetchInspectionScheduleById(id: string) {
    return this.get(`/inspection-schedules/${id}`)
  }

  async createInspectionSchedule(data: any) {
    return this.post('/inspection-schedules', data)
  }

  async updateInspectionSchedule(id: string, data: any) {
    return this.put(`/inspection-schedules/${id}`, data)
  }

  async deleteInspectionSchedule(id: string) {
    return this.delete(`/inspection-schedules/${id}`)
  }

  async fetchInspectionSchedulesByEquipment(equipmentId: string) {
    return this.get(`/inspection-schedules/by-equipment/${equipmentId}`)
  }

  // Inspection Parameters (Checklist Items) API methods
  async fetchInspectionParameters(filters: Record<string, any> = {}) {
    return this.get('/inspection-parameters', { params: filters })
  }

  async fetchInspectionParameterById(id: string) {
    return this.get(`/inspection-parameters/${id}`)
  }

  async fetchInspectionParametersByEquipmentType(equipmentTypeId: string) {
    return this.get('/inspection-parameters', { params: { equipmentTypeId } })
  }

  async createInspectionParameter(data: any) {
    return this.post('/inspection-parameters', data)
  }

  async updateInspectionParameter(id: string, data: any) {
    return this.put(`/inspection-parameters/${id}`, data)
  }

  async deleteInspectionParameter(id: string) {
    return this.delete(`/inspection-parameters/${id}`)
  }

  // Maintenance API methods
  async fetchMaintenanceTickets(filters: Record<string, any> = {}) {
    return this.get('/maintenance-tickets', { params: filters })
  }

  async fetchMaintenanceTicketById(id: string) {
    return this.get(`/maintenance-tickets/${id}`)
  }

  async createMaintenanceTicket(data: any) {
    return this.post('/maintenance-tickets', data)
  }

  async updateMaintenanceTicket(id: string, data: any) {
    return this.put(`/maintenance-tickets/${id}`, data)
  }

  async updateMaintenanceTicketStatus(id: string, status: string, data?: any) {
    return this.put(`/maintenance-tickets/${id}/status`, { status, ...data })
  }

  async assignMaintenanceTicket(id: string, assignedTo: string) {
    return this.put(`/maintenance-tickets/${id}/assign`, { assignedTo })
  }

  async fetchMaintenanceTicketsByEquipment(equipmentId: string) {
    return this.get(`/maintenance-tickets/by-equipment/${equipmentId}`)
  }

  async fetchMaintenanceTicketsByTechnician(technicianId: string) {
    return this.get(`/maintenance-tickets/by-technician/${technicianId}`)
  }

  async fetchOverdueMaintenanceTickets() {
    return this.get('/maintenance-tickets/overdue')
  }

  async fetchMaintenanceStats() {
    return this.get('/maintenance-tickets/stats')
  }

  async fetchMaintenanceHistory(equipmentId: string) {
    return this.get(`/maintenance-tickets/history/${equipmentId}`)
  }

  async deleteMaintenanceTicket(id: string) {
    return this.delete(`/maintenance-tickets/${id}`)
  }

  async updateMaintenanceTicketHours(id: string, hours: number) {
    return this.put(`/maintenance-tickets/${id}/hours`, { actualHours: hours })
  }

  async completeMaintenanceTicket(id: string, resolutionNotes: string, actualHours?: number) {
    return this.put(`/maintenance-tickets/${id}/status`, {
      status: 'completed',
      resolutionNotes,
      actualHours,
    })
  }

  async recordTicketInventory(id: string, items: Array<{ itemId: string; quantity: number }>) {
    return this.post(`/maintenance-tickets/${id}/inventory`, { items })
  }

  // Inventory API methods
  async fetchInventory(filters: Record<string, any> = {}) {
    return this.get('/inventory', { params: filters })
  }

  async fetchInventoryById(id: string) {
    return this.get(`/inventory/${id}`)
  }

  async createInventoryItem(data: any) {
    return this.post('/inventory', data)
  }

  async updateInventoryItem(id: string, data: any) {
    return this.put(`/inventory/${id}`, data)
  }

  async deleteInventoryItem(id: string) {
    return this.delete(`/inventory/${id}`)
  }

  async fetchInventoryByCategory(categoryId: string) {
    return this.get(`/inventory/by-category/${categoryId}`)
  }

  async fetchLowStockItems() {
    return this.get('/inventory/low-stock')
  }

  async fetchInventoryStats() {
    return this.get('/inventory/stats')
  }

  async updateInventoryStock(id: string, quantity: number, type: 'add' | 'remove', reason?: string) {
    return this.post(`/inventory/${id}/stock`, { quantity, type, reason })
  }

  async fetchInventoryTransactions(filters: Record<string, any> = {}) {
    return this.get('/inventory/transactions', { params: filters })
  }

  // Inventory Category API methods
  async fetchInventoryCategories() {
    return this.get('/inventory/categories')
  }

  async createInventoryCategory(data: any) {
    return this.post('/inventory/categories', data)
  }

  async updateInventoryCategory(id: string, data: any) {
    return this.put(`/inventory/categories/${id}`, data)
  }

  async deleteInventoryCategory(id: string) {
    return this.delete(`/inventory/categories/${id}`)
  }

  // User API methods
  async fetchUsers(filters: Record<string, any> = {}) {
    return this.get('/users', { params: filters })
  }

  async fetchUserById(id: string) {
    return this.get(`/users/${id}`)
  }

  async createUser(data: any) {
    return this.post('/users', data)
  }

  async updateUser(id: string, data: any) {
    return this.put(`/users/${id}`, data)
  }

  async deleteUser(id: string) {
    return this.delete(`/users/${id}`)
  }

  async fetchUsersByRole(role: string) {
    return this.get(`/users/by-role/${role}`)
  }

  async fetchUsersByDepartment(departmentId: string) {
    return this.get(`/users/by-department/${departmentId}`)
  }

  async updateUserRole(id: string, role: string) {
    return this.put(`/users/${id}/role`, { role })
  }

  async fetchUserStats() {
    return this.get('/users/stats')
  }

  async changePassword(data: { currentPassword: string; newPassword: string }) {
    return this.put<{ success: boolean; message?: string }>('/users/change-password', data)
  }

  async changeUserPassword(userId: string, password: string) {
    return this.put<{ success: boolean; message?: string }>(`/users/${userId}/password`, { password })
  }

  async resetUserPassword(userId: string) {
    return this.post<{ success: boolean; temporaryPassword?: string; message?: string }>(`/users/${userId}/reset-password`, {})
  }

  // Tenant API methods (require super admin access)
  private getSuperAdminHeaders() {
    return {
      'x-super-admin-key': 'super-admin-key-change-me'
    }
  }

  async fetchTenants(filters: Record<string, any> = {}) {
    return this.get('/tenants', {
      params: filters,
      headers: this.getSuperAdminHeaders()
    })
  }

  async fetchTenantById(id: string) {
    return this.get(`/tenants/${id}`, {
      headers: this.getSuperAdminHeaders()
    })
  }

  async createTenant(data: any) {
    return this.post('/tenants', data, {
      headers: this.getSuperAdminHeaders()
    })
  }

  async updateTenant(id: string, data: any) {
    return this.put(`/tenants/${id}`, data, {
      headers: this.getSuperAdminHeaders()
    })
  }

  async deleteTenant(id: string) {
    return this.delete(`/tenants/${id}`, {
      headers: this.getSuperAdminHeaders()
    })
  }

  async fetchTenantStats(id: string) {
    return this.get(`/tenants/${id}/stats`, {
      headers: this.getSuperAdminHeaders()
    })
  }

  // Department API methods
  async fetchDepartments(filters: Record<string, any> = {}) {
    return this.get('/departments', { params: filters })
  }

  async fetchDepartmentById(id: string) {
    return this.get(`/departments/${id}`)
  }

  async createDepartment(data: any) {
    return this.post('/departments', data)
  }

  async updateDepartment(id: string, data: any) {
    return this.put(`/departments/${id}`, data)
  }

  async deleteDepartment(id: string) {
    return this.delete(`/departments/${id}`)
  }

  async fetchDepartmentsByManager(managerId: string) {
    return this.get(`/departments/by-manager/${managerId}`)
  }

  // Reports API methods
  async fetchReports(filters: Record<string, any> = {}) {
    return this.get('/reports', { params: filters })
  }

  async generateReport(config: any) {
    return this.post('/reports/generate', config)
  }

  async fetchReportTemplates() {
    return this.get('/reports/templates')
  }

  async createReportTemplate(template: any) {
    return this.post('/reports/templates', template)
  }

  async updateReportTemplate(id: string, template: any) {
    return this.put(`/reports/templates/${id}`, template)
  }

  async deleteReportTemplate(id: string) {
    return this.delete(`/reports/templates/${id}`)
  }

  async fetchReportHistory(params?: { limit?: number; offset?: number }) {
    return this.get('/reports/history', { params })
  }

  async downloadReport(reportId: string) {
    return this.get<Blob>(`/reports/${reportId}/download`, { responseType: 'blob' })
  }

  async deleteReportHistory(reportId: string) {
    return this.delete(`/reports/history/${reportId}`)
  }

  async fetchScheduledReports() {
    return this.get('/reports/scheduled')
  }

  async createScheduledReport(scheduledReport: any) {
    return this.post('/reports/scheduled', scheduledReport)
  }

  async updateScheduledReport(id: string, scheduledReport: any) {
    return this.put(`/reports/scheduled/${id}`, scheduledReport)
  }

  async deleteScheduledReport(id: string) {
    return this.delete(`/reports/scheduled/${id}`)
  }

  async runScheduledReport(reportId: string) {
    return this.post(`/reports/scheduled/${reportId}/run`)
  }

  // Checklist API methods
  async fetchChecklists(filters: Record<string, any> = {}) {
    return this.get('/checklists', { params: filters })
  }

  async fetchChecklistById(id: string) {
    return this.get(`/checklists/${id}`)
  }

  async createChecklist(data: any) {
    return this.post('/checklists', data)
  }

  async updateChecklist(id: string, data: any) {
    return this.put(`/checklists/${id}`, data)
  }

  async deleteChecklist(id: string) {
    return this.delete(`/checklists/${id}`)
  }

  async toggleChecklistStatus(id: string, isActive: boolean) {
    return this.patch(`/checklists/${id}/status`, { isActive })
  }

  async duplicateChecklist(id: string) {
    return this.post(`/checklists/${id}/duplicate`)
  }

  async fetchChecklistResponses(filters: Record<string, any> = {}) {
    return this.get('/checklists/responses', { params: filters })
  }

  async fetchChecklistResponseById(id: string) {
    return this.get(`/checklists/responses/${id}`)
  }

  async fetchChecklistStats(equipmentId?: string) {
    const url = equipmentId ? `/checklists/stats/${equipmentId}` : '/checklists/stats'
    return this.get(url)
  }

  async fetchChecklistHistory(equipmentId: string) {
    return this.get(`/checklists/history/${equipmentId}`)
  }

  async fetchChecklistTrends(equipmentId: string, days: number = 90) {
    return this.get(`/checklists/stats/equipment/${equipmentId}/trends`, { params: { days } })
  }

  async fetchChecklistOverdue(limit: number = 50) {
    return this.get('/checklists/stats/overdue', { params: { limit } })
  }

  async fetchChecklistStatsByType(days: number = 30) {
    return this.get('/checklists/stats/by-type', { params: { days } })
  }

  async fetchChecklistStatsByChecklist(checklistId: string, days: number = 30) {
    return this.get(`/checklists/stats/by-checklist/${checklistId}`, { params: { days } })
  }

  async fetchUserChecklistStats(userId: string, days: number = 30) {
    return this.get(`/checklists/stats/by-user/${userId}`, { params: { days } })
  }

  async exportChecklistData(params: Record<string, any>) {
    return this.get('/checklists/export', { params })
  }

  // Upload file to OneDrive
  async uploadFileToOneDrive(file: File | Blob, taskId: string, taskType: string, description?: string) {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('taskId', taskId)
    formData.append('taskType', taskType)
    if (description) {
      formData.append('description', description)
    }

    return this.post<any>('/files/upload-onedrive', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  }

  // Bulk Import & Export API Methods
  async bulkImportEquipment(items: any[], mode: 'upsert' | 'insertOnly' = 'upsert') {
    return this.post<any>('/equipment/bulk-import', { items, mode })
  }

  async exportAllEquipment() {
    return this.get<any>('/equipment/export-all')
  }

  async bulkImportEquipmentTypes(items: any[], mode: 'upsert' | 'insertOnly' = 'upsert') {
    return this.post<any>('/equipment-types/bulk-import', { items, mode })
  }

  async exportAllEquipmentTypes() {
    return this.get<any>('/equipment-types/export-all')
  }

  async bulkImportDepartments(items: any[], mode: 'upsert' | 'insertOnly' = 'upsert') {
    return this.post<any>('/departments/bulk-import', { items, mode })
  }

  async exportAllDepartments() {
    return this.get<any>('/departments/export-all')
  }

  async bulkImportInventory(items: any[], mode: 'upsert' | 'insertOnly' = 'upsert') {
    return this.post<any>('/inventory/bulk-import', { items, mode })
  }

  async exportAllInventory() {
    return this.get<any>('/inventory/export-all')
  }

  async bulkImportUsers(items: any[], mode: 'upsert' | 'insertOnly' = 'upsert') {
    return this.post<any>('/users/bulk-import', { items, mode })
  }

  async exportAllUsers() {
    return this.get<any>('/users/export-all')
  }

  async bulkImportChecklists(items: any[], mode: 'upsert' | 'insertOnly' = 'upsert') {
    return this.post<any>('/checklists/bulk-import', { items, mode })
  }

  async exportAllChecklists() {
    return this.get<any>('/checklists/export-all')
  }

  // Health check
  async healthCheck() {
    return this.get('/health')
  }
}

// Export singleton instance
export const apiService = new ApiService()
export default apiService